import type React from 'react'

export type ChartNumberFormatter = (value: number) => string

export const defaultNumberFormatter: ChartNumberFormatter = (value) => {
 const n = Number(value ?? 0)
 if (!Number.isFinite(n)) return '0'
 return n.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

/** Fixed hex palette — MUI/d3-color cannot parse CSS var tokens like "222 47% 11%". */
const LIGHT_CHART_PALETTE = [
 '#2563EB',
 '#14B8A6',
 '#F59E0B',
 '#64748B',
 '#1E293B',
 '#60A5FA',
 '#94A3B8',
 '#475569',
] as const

const DARK_CHART_PALETTE = [
 '#3B82F6',
 '#22C55E',
 '#F59E0B',
 '#A8B3C5',
 '#60A5FA',
 '#2DD4BF',
 '#6F7C91',
 '#18243A',
] as const

function isDarkMode(): boolean {
 if (typeof document === 'undefined') return false
 const el = document.documentElement
 return el.classList.contains('dark') || el.getAttribute('data-theme') === 'dark'
}

export function getChartPalette(): string[] {
 return isDarkMode() ? [...DARK_CHART_PALETTE] : [...LIGHT_CHART_PALETTE]
}

export function getChartAxisColors() {
 const dark = isDarkMode()
 return {
  grid: dark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(148, 163, 184, 0.12)',
  label: dark ? '#A8B3C5' : '#64748b',
 }
}

export const CHART_ZERO_BAR_COLOR = '#E8EDF3'
export const CHART_ZERO_BAR_COLOR_DARK = '#18243A'

export function getZeroBarColor(): string {
 return isDarkMode() ? CHART_ZERO_BAR_COLOR_DARK : CHART_ZERO_BAR_COLOR
}

export function chartCardGradientBackgroundCss(): string {
 const dark = isDarkMode()
 if (dark) {
  return 'linear-gradient(135deg, hsl(var(--primary) / 0.06) 0%, hsl(var(--muted) / 0.4) 45%, hsl(var(--background) / 0.2) 100%)'
 }
 return 'linear-gradient(135deg, hsl(222 47% 11% / 0.04) 0%, hsl(215 25% 27% / 0.04) 45%, hsl(215 20% 35% / 0.04) 100%)'
}

export function getHorizontalCategoryReserve(categories: string[], max = 200): number {
 const longest = categories.reduce((maxLen, label) => Math.max(maxLen, String(label ?? '').length), 0)
 return Math.min(max, Math.max(64, Math.round(longest * 6.5 + 24)))
}

export function estimateYAxisWidth(
 values: number[],
 formatter: ChartNumberFormatter = defaultNumberFormatter,
 min = 48,
 max = 80,
): number {
 const finite = values.filter((v) => Number.isFinite(v))
 if (finite.length === 0) return min

 const maxVal = Math.max(...finite, 0)
 const minVal = Math.min(...finite, 0)
 const samples = [0, maxVal, minVal, maxVal / 2, maxVal * 0.75].filter((v) => Number.isFinite(v))
 const longest = samples.reduce((acc, v) => Math.max(acc, formatter(v).length), 0)
 return Math.min(max, Math.max(min, Math.round(longest * 6.5 + 16)))
}

export function buildSparseTickLabelInterval(
 count: number,
 maxTicks = 8,
): 'auto' | ((value: unknown, index: number) => boolean) {
 if (count <= maxTicks) return 'auto'
 const step = Math.ceil(count / maxTicks)
 return (_value, index) => index === 0 || index === count - 1 || index % step === 0
}

export type AreaGradientDef = { id: string; color: string }

export function buildAreaGradientDefs(colors: string[]): AreaGradientDef[] {
 return colors.map((color, i) => ({ id: `erp-area-gradient-${i}`, color }))
}

export function buildAreaGradientSx(seriesCount: number): Record<string, { fill: string }> {
 const sx: Record<string, { fill: string }> = {}
 for (let i = 0; i < seriesCount; i += 1) {
  sx[`& .MuiAreaElement-root.series-${i}`] = { fill: `url(#erp-area-gradient-${i})` }
 }
 return sx
}

export function getShowMarkStrategy(
 pointCount: number,
): boolean | ((params: { index: number }) => boolean) {
 if (pointCount <= 14) return true
 return (params) => params.index === pointCount - 1
}

export function defaultChartMargins(opts: {
 yAxisWidth: number
 showLegend?: boolean
 horizontal?: boolean
}) {
 const { yAxisWidth, showLegend = false, horizontal = false } = opts
 return {
  left: yAxisWidth + 8,
  right: 16,
  top: 16,
  bottom: showLegend ? 48 : horizontal ? 24 : 32,
 }
}

export const sharedChartSx = {
 '& .MuiChartsAxis-line': { stroke: 'var(--chart-grid, rgba(148,163,184,0.12))' },
 '& .MuiChartsAxis-tick': { stroke: 'var(--chart-grid, rgba(148,163,184,0.12))' },
 '& .MuiChartsAxis-tickLabel': {
  fill: 'var(--chart-label, #64748b) !important',
  fontFamily: 'inherit',
  fontSize: '11px',
 },
 '& .MuiChartsLegend-label': {
  fill: 'var(--chart-label, #64748b) !important',
  fontFamily: 'inherit',
  fontSize: '12px',
 },
} as const

export function chartCssVarsStyle(): React.CSSProperties {
 const { grid, label } = getChartAxisColors()
 return {
  width: '100%',
  minWidth: 0,
  ['--chart-grid' as string]: grid,
  ['--chart-label' as string]: label,
 }
}

/** MUI theme palette for ChartsProvider */
export function getMuiChartThemeColors(mode: 'light' | 'dark') {
 const palette = mode === 'dark' ? DARK_CHART_PALETTE : LIGHT_CHART_PALETTE
 return {
  primary: { main: palette[0], light: palette[5], dark: palette[4] },
  secondary: { main: palette[1] },
  success: { main: palette[1] },
  warning: { main: palette[2] },
  info: { main: palette[0] },
 }
}
